import React from "react";
import { theme, Typography } from "antd";
import { RightOutlined } from "@ant-design/icons";
import { formatHora, relativeTime } from "../../utils/relativeTime";
import { TIPOS_EVENTO } from "./notificacionesConfig";
import { iniciaConNombre, nombrePropio, textoEvento, textoPrincipal, tipoEvento } from "./notificacionesUtils";
import "./notificaciones.css";

const { Text } = Typography;

// Bloque compartido: cuadro de ícono del módulo, texto y chip de estado.
function FilaBase({ item, onAbrir, tituloNode, subtitulo, derecha, ariaLabel, navegable: navegableProp }) {
  const { token } = theme.useToken();
  const navegable = navegableProp ?? Boolean(item.ruta);
  const tinte = !item.leido ? token.colorPrimaryBg : token.colorBgContainer;

  const manejarTecla = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      if (navegable) onAbrir(item);
    }
  };

  return (
    <div
      className="nt-fila"
      role={navegable ? "link" : undefined}
      tabIndex={navegable ? 0 : undefined}
      aria-label={ariaLabel}
      aria-disabled={!navegable ? true : undefined}
      onClick={() => navegable && onAbrir(item)}
      onKeyDown={manejarTecla}
      style={{
        minHeight: 68,
        padding: "12px 16px",
        background: tinte,
        cursor: navegable ? "pointer" : "default",
      }}
    >
      <span
        aria-label={item.leido ? "Leída" : "No leída"}
        role="img"
        className="nt-punto"
        style={{ background: item.leido ? "transparent" : token.colorPrimary }}
      />

      <span
        role="img"
        aria-label={item.nombreModulo}
        className="nt-icono-modulo"
        style={{ background: token.colorPrimaryBg, color: token.colorPrimary }}
      >
        <i className={item.icono} aria-hidden="true" />
      </span>

      <div className="nt-texto">
        <Text
          style={{
            display: "block",
            fontWeight: item.leido ? 400 : 500,
            color: item.leido ? token.colorTextSecondary : token.colorText,
          }}
        >
          {tituloNode}
        </Text>
        <Text type="secondary" style={{ fontSize: 13 }}>
          {subtitulo}
        </Text>
      </div>

      <div className="nt-derecha">{derecha}</div>
    </div>
  );
}

// Chip de estado: ícono + texto. Si el estado no está en el mapeo cae en gris.
export function EstadoChip({ estado }) {
  return (
    <span
      className="nt-chip"
      style={{ background: estado.bg, color: estado.color }}
    >
      <i className={estado.icon} aria-hidden="true" />
      <span>{estado.label}</span>
    </span>
  );
}

function Subtitulo({ item }) {
  const partes = [item.nombreModulo, item.ciudadNombre].filter(Boolean);
  return (
    <>
      {partes.join(" · ")}
      {item.nombreUsuario ? (
        <>
          {partes.length > 0 ? " · " : ""}
          por <span className="nt-nombre-usuario">{nombrePropio(item.nombreUsuario)}</span>
        </>
      ) : null}
    </>
  );
}

function TextoConExpediente({ item, rafaga = false }) {
  const principal = textoPrincipal(item);
  const mostrarNombre = iniciaConNombre(item);
  const resto = mostrarNombre ? principal.slice(item.nombreUsuario.length) : principal;
  return (
    <>
      {mostrarNombre ? <strong className="nt-nombre-usuario-titulo">{nombrePropio(item.nombreUsuario)}</strong> : null}
      {resto}
      {!rafaga && item.expediente ? ` · Expediente ${item.expediente}` : null}
    </>
  );
}

// Fila de una notificación individual.
export function NotificacionSimple({ item, onAbrir }) {
  return (
    <FilaBase
      item={item}
      onAbrir={onAbrir}
      ariaLabel={textoPrincipal(item)}
      tituloNode={<TextoConExpediente item={item} />}
      subtitulo={<Subtitulo item={item} />}
      derecha={
        <>
          <EstadoChip estado={item.estado} />
          <Text type="secondary" className="nt-hora" title={relativeTime(item.fecha)}>
            {formatHora(item.fecha)}
          </Text>
          <RightOutlined
            aria-hidden="true"
            className="nt-chevron"
            style={{ opacity: item.ruta ? 1 : 0.3 }}
          />
        </>
      }
    />
  );
}

// Ráfaga: varios eventos del mismo usuario sobre el mismo expediente. Se
// muestran siempre todos los cambios con su detalle específico (sin acordeón
// que los oculte).
export function NotificacionRafaga({ items, onAbrir, onExpandir }) {
  const { token } = theme.useToken();
  const primero = items[0];
  const hayNoLeidas = items.some((i) => !i.leido);
  const referencia = primero.expediente ?? `#${primero.idKey}`;

  const abrirRafaga = () => {
    if (hayNoLeidas) onExpandir(items);
    if (primero.ruta) onAbrir(primero);
  };

  const tituloNode = (
    <>
      {primero.nombreUsuario ? (
        <strong className="nt-nombre-usuario-titulo">{nombrePropio(primero.nombreUsuario)}</strong>
      ) : null}
      {primero.nombreUsuario ? " " : null}
      {`hizo ${items.length} cambios en el Expediente ${referencia}`}
    </>
  );

  return (
    <div>
      <FilaBase
        item={{ ...primero, leido: !hayNoLeidas }}
        onAbrir={abrirRafaga}
        navegable={Boolean(primero.ruta)}
        ariaLabel={`${items.length} cambios en el expediente ${referencia}`}
        tituloNode={tituloNode}
        subtitulo={<Subtitulo item={primero} />}
        derecha={
          <>
            <EstadoChip estado={primero.estado} />
            <Text type="secondary" className="nt-hora" title={relativeTime(primero.fecha)}>
              {formatHora(primero.fecha)}
            </Text>
          </>
        }
      />

      <div className="nt-eventos" style={{ borderTop: `1px solid ${token.colorSplit}` }}>
        {items.map((ev) => {
          const tipo = TIPOS_EVENTO[tipoEvento(ev.descripcion)];
          const color = token[tipo.colorKey];
          const navegable = Boolean(ev.ruta);
          const abrirEvento = () => {
            if (!ev.leido) onExpandir([ev]);
            if (navegable) onAbrir(ev);
          };
          return (
            <div
              key={ev.id}
              className="nt-evento"
              role={navegable ? "link" : undefined}
              tabIndex={navegable ? 0 : undefined}
              onClick={abrirEvento}
              onKeyDown={(e) => {
                if ((e.key === "Enter" || e.key === " ") && navegable) {
                  e.preventDefault();
                  abrirEvento();
                }
              }}
              style={{ cursor: navegable ? "pointer" : "default" }}
            >
              <i className={tipo.icon} aria-hidden="true" style={{ color, width: 18 }} />
              <Text style={{ flex: 1, minWidth: 0 }}>{textoEvento(ev)}</Text>
              <Text type="secondary" style={{ fontSize: 13 }} title={relativeTime(ev.fecha)}>
                {formatHora(ev.fecha)}
              </Text>
            </div>
          );
        })}
      </div>
    </div>
  );
}
